import { Box } from '@mui/material'
import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet'

import Content from 'components/common/Content'
import PageSection from 'components/common/PageSection'
import { Header } from 'components/navigation/Header'
import { TestSuitesTable } from 'components/test-suites/list/TestSuitesTable'
import {
  getCanCreateAndEditTestSuites,
  isGremlinOfficialTestSuite,
  sortTestSuitesWithGremlinOfficialAndCurrentlyUsed
} from 'components/test-suites/test-suites-utils'
import { TestSuitesBreadcrumbs } from 'components/test-suites/TestSuitesBreadcrumbs'
import { TestSuitesHeader } from 'components/test-suites/TestSuitesHeader'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { useHasFlagChangedFrom } from 'hooks/use-has-flag-changed'
import { useTranslation } from 'hooks/use-translation'
import { TestSuite } from 'types/api/test-suites/test-suites'
import { createLookupMapFromArray } from 'utils/obj'

const testSuitesPlaceholder = [] as TestSuite[]

export const TestSuitesPage: React.FC = () => {
  const { trans } = useTranslation()
  const { currentTeamId, hasEndpointAccess, teams: teamsAccess } = useAuth()

  const canCreateAndEditTestSuites =
    getCanCreateAndEditTestSuites(hasEndpointAccess)

  const {
    data: testSuites = testSuitesPlaceholder,
    isFetching: isLoadingTestSuites,
    isError: isErrorTestSuites,
    refetch: fetchTestSuites
  } = useGremlinQuery<TestSuite[]>(
    '/test-suites',
    {},
    {
      placeholderData: testSuitesPlaceholder,
      refetchInterval: 60_000
    }
  )

  const isLoading = isLoadingTestSuites
  // track whether we've loaded once so we don't re-display loading state when refetching data
  const hasLoadedOnce = useHasFlagChangedFrom({
    flag: isLoading,
    expectedStartValue: true
  })
  const isLoadingFirstTime = isLoading && !hasLoadedOnce

  const isError = isErrorTestSuites

  const filteredOrderedTestSuites = useMemo(() => {
    if (canCreateAndEditTestSuites) {
      return sortTestSuitesWithGremlinOfficialAndCurrentlyUsed(
        testSuites,
        currentTeamId
      )
    }

    const teamsAccessIdMap = createLookupMapFromArray(teamsAccess, 'identifier')

    // only show official test suite and test suites of teams the user has access to
    return sortTestSuitesWithGremlinOfficialAndCurrentlyUsed(
      testSuites.filter(
        (testSuite) =>
          isGremlinOfficialTestSuite(testSuite) ||
          testSuite.targetTeamIds.some((teamId) =>
            Boolean(teamsAccessIdMap[teamId])
          )
      ),
      currentTeamId
    )
  }, [currentTeamId, canCreateAndEditTestSuites, teamsAccess, testSuites])

  return (
    <>
      <Helmet title={trans('test_suites')} />
      <Header>
        <TestSuitesBreadcrumbs onRootPage={true} />
      </Header>
      <Content
        css={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridAutoRows: 'min-content'
        }}
      >
        <TestSuitesHeader />

        <PageSection
          errorMessage={trans('test_suites.list.loading_error')}
          hasError={isError}
          isLoading={isLoadingFirstTime}
        >
          {() => {
            return (
              <Box mt={4}>
                <TestSuitesTable
                  fetchTestSuites={fetchTestSuites}
                  testSuites={filteredOrderedTestSuites}
                />
              </Box>
            )
          }}
        </PageSection>
      </Content>
    </>
  )
}
